import dayjs from 'dayjs';
import { Calendar, Clock9, Headset, PrinterCheck } from 'lucide-react';
import { round } from '@/utils/mathUtils';

import { Card, CardContent } from '@/components/ui/card';
import type { IStoreOutToCustomerHeader } from '@/Shared/SaleInvoiceDialog/api/salesInvoice.interface';

type Props = {
  invoiceHeader: IStoreOutToCustomerHeader;
  oldData: {
    perviousRequests: { ID: number }[];
    perviousIncoming: { ID: number }[];
  };
};

function InvoiceHeader({ invoiceHeader, oldData }: Props) {
  return (
    <div className="relative overflow-hidden rounded-xl border border-indigo-200/30 bg-gradient-to-br from-white/95 via-white/95 to-white/90 shadow-xl transition-all hover:shadow-md dark:border-indigo-700/30 dark:from-gray-900/95 dark:via-gray-800/95 dark:to-gray-800/90">
      {/* Subtle shimmer effect */}
      <div
        className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/5 to-transparent"
        style={{ animation: 'shimmer 8s infinite' }}
      ></div>

      {/* Pattern overlay */}
      <div
        className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05]"
        style={{
          backgroundImage: `
            radial-gradient(#4f46e5 0.75px, transparent 0.75px), 
            radial-gradient(#4f46e5 0.75px, transparent 0.75px)
          `,
          backgroundSize: '20px 20px',
          backgroundPosition: '0 0, 10px 10px',
        }}
      />

      {/* Royal border effect */}
      <div className="absolute inset-0 rounded-xl border border-indigo-300/20 dark:border-indigo-700/20"></div>
      <div className="absolute inset-[3px] rounded-lg border border-amber-300/20 dark:border-amber-700/20"></div>

      <Card className="border-0 bg-transparent shadow-none">
        <CardContent className="relative p-3">
          <div className="flex flex-col">
            {/* Decorative corner accents */}
            <div className="absolute left-2 top-2 h-6 w-6 rounded-br-xl border-b border-r border-indigo-200/30 dark:border-indigo-700/30"></div>
            <div className="absolute bottom-2 right-2 h-6 w-6 rounded-tl-xl border-l border-t border-indigo-200/30 dark:border-indigo-700/30"></div>

            {/* Header Section with Invoice ID and Client */}
            <div className="mb-3 flex-1 md:text-start">
              <div className="flex flex-col items-start gap-1 sm:flex-row sm:items-center sm:justify-between">
                <h3 className="text-2xl font-bold tracking-tight text-amber-700 dark:text-amber-400">
                  فاتورة{' '}
                  <span className="relative">
                    <span className="relative z-10 text-3xl font-extrabold">
                      #{invoiceHeader?.ID}
                    </span>
                    <span className="absolute -bottom-1 left-0 z-0 h-2 w-full bg-amber-200/30 dark:bg-amber-700/30"></span>
                  </span>
                </h3>
                <div className="rounded-full bg-gradient-to-r from-amber-100/80 to-amber-200/80 px-3 py-1 text-xs font-semibold text-amber-800 shadow-sm dark:from-amber-900/30 dark:to-amber-800/30 dark:text-amber-300">
                  {invoiceHeader?.StoreName}
                </div>
              </div>

              <div className="mt-3 flex flex-col">
                <div className="text-xl font-semibold text-indigo-600 dark:text-indigo-400">
                  {invoiceHeader?.ClientName}
                </div>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  <span className="rounded-full bg-gradient-to-r from-indigo-100/80 to-indigo-200/80 px-2.5 py-0.5 text-xs font-medium text-indigo-700 shadow-sm dark:from-indigo-900/30 dark:to-indigo-800/30 dark:text-indigo-300">
                    {invoiceHeader?.ClasssName}
                  </span>
                  <span className="rounded-full bg-gradient-to-r from-indigo-100/80 to-indigo-200/80 px-2.5 py-0.5 text-xs font-medium text-indigo-700 shadow-sm dark:from-indigo-900/30 dark:to-indigo-800/30 dark:text-indigo-300">
                    {invoiceHeader?.RegionName}
                  </span>
                </div>
              </div>

              {/* Info Section with Date, Time, Employee */}
              <div className="mt-3 flex flex-col space-y-1.5">
                <div className="flex items-center gap-2">
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-gray-100 to-gray-200 shadow-sm dark:from-gray-800 dark:to-gray-700">
                    <Calendar className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400">
                      التاريخ:
                    </span>
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-200">
                      {dayjs(new Date(invoiceHeader?.TrDate)).format(
                        'YYYY-MM-DD',
                      )}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-gray-100 to-gray-200 shadow-sm dark:from-gray-800 dark:to-gray-700">
                    <Clock9 className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400">
                      الوقت:
                    </span>
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-200">
                      {dayjs(new Date(invoiceHeader?.TrTime)).format('HH:mm')}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-gray-100 to-gray-200 shadow-sm dark:from-gray-800 dark:to-gray-700">
                    <Headset className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400">
                      الموظف:
                    </span>
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-200">
                      {invoiceHeader?.EmpName}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-gray-100 to-gray-200 shadow-sm dark:from-gray-800 dark:to-gray-700">
                    <PrinterCheck className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400">
                      مرات الطباعة:
                    </span>
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-200">
                      {invoiceHeader?.PrintTimes}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Financial Summary Section */}
            <div className="mt-3 overflow-hidden rounded-lg border border-indigo-200/30 bg-gradient-to-r from-indigo-50/80 to-purple-50/80 shadow-sm dark:border-indigo-700/30 dark:from-indigo-900/20 dark:to-purple-900/20">
              <div className="rounded-md bg-white/50 p-2.5 backdrop-blur-sm dark:bg-gray-800/50">
                <h4 className="mb-2 flex items-center gap-2 text-sm font-medium text-indigo-700 dark:text-indigo-300">
                  <span className="inline-block h-1 w-4 rounded-full bg-indigo-600 dark:bg-indigo-400"></span>
                  ملخص مالي
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex flex-col rounded-lg bg-white/80 p-2 shadow-sm dark:bg-gray-800/80">
                    <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400">
                      الشريحة
                    </span>
                    <span className="text-xs font-medium text-gray-800 dark:text-gray-200">
                      {invoiceHeader?.DiscountTypeName || 'لا يوجد'}
                    </span>
                  </div>

                  <div className="flex flex-col rounded-lg bg-white/80 p-2 shadow-sm dark:bg-gray-800/80">
                    <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400">
                      الحساب قبل
                    </span>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-800 dark:text-gray-200">
                        {Math.abs(
                          round(invoiceHeader?.C_Balance, 2),
                        ).toLocaleString()}
                      </span>
                      <span
                        className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                          invoiceHeader?.C_Balance < 0
                            ? 'bg-gradient-to-r from-red-100 to-red-200 text-red-700 dark:from-red-900/30 dark:to-red-800/30 dark:text-red-300'
                            : invoiceHeader?.C_Balance > 0
                              ? 'bg-gradient-to-r from-green-100 to-green-200 text-green-700 dark:from-green-900/30 dark:to-green-800/30 dark:text-green-300'
                              : 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 dark:from-gray-800 dark:to-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {invoiceHeader?.C_Balance < 0
                          ? 'مدين'
                          : invoiceHeader?.C_Balance > 0
                            ? 'دائن'
                            : 'خالص'}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col rounded-lg bg-white/80 p-2 shadow-sm dark:bg-gray-800/80">
                    <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400">
                      خصم إضافي
                    </span>
                    <span className="text-xs font-medium text-gray-800 dark:text-gray-200">
                      {round(invoiceHeader?.AddDiscount, 2).toLocaleString()}
                    </span>
                  </div>

                  <div className="flex flex-col rounded-lg bg-gradient-to-r from-indigo-100/80 to-indigo-200/80 p-2 shadow-sm dark:from-indigo-900/40 dark:to-indigo-800/40">
                    <span className="text-[10px] font-medium text-indigo-700 dark:text-indigo-300">
                      إجمالي الفاتورة
                    </span>
                    <span className="text-xs font-bold text-indigo-800 dark:text-indigo-200">
                      {round(invoiceHeader?.TotalInvoice, 2).toLocaleString()}
                    </span>
                  </div>

                  <div className="col-span-2 flex flex-col rounded-lg bg-white/80 p-2 shadow-sm dark:bg-gray-800/80">
                    <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400">
                      الحساب بعد
                    </span>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-800 dark:text-gray-200">
                        {Math.abs(
                          round(invoiceHeader?.C_BalanceAfter, 2),
                        ).toLocaleString()}
                      </span>
                      <span
                        className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                          invoiceHeader?.C_BalanceAfter < 0
                            ? 'bg-gradient-to-r from-red-100 to-red-200 text-red-700 dark:from-red-900/30 dark:to-red-800/30 dark:text-red-300'
                            : invoiceHeader?.C_BalanceAfter > 0
                              ? 'bg-gradient-to-r from-green-100 to-green-200 text-green-700 dark:from-green-900/30 dark:to-green-800/30 dark:text-green-300'
                              : 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 dark:from-gray-800 dark:to-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {invoiceHeader?.C_BalanceAfter < 0
                          ? 'مدين'
                          : invoiceHeader?.C_BalanceAfter > 0
                            ? 'دائن'
                            : 'خالص'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Related Orders Section */}
            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
              <div className="rounded-lg border border-amber-200/30 bg-white/80 shadow-sm dark:border-amber-700/30 dark:bg-gray-800/80">
                <div className="rounded-md p-2.5">
                  <h4 className="mb-2 flex items-center gap-2 text-sm font-medium text-amber-700 dark:text-amber-400">
                    <span className="inline-block h-1 w-4 rounded-full bg-amber-600 dark:bg-amber-400"></span>
                    الطلبيات
                  </h4>
                  {oldData?.perviousRequests.length === 0 ? (
                    <div className="flex h-8 items-center justify-center rounded-md bg-gray-50/80 text-xs text-gray-500 dark:bg-gray-700/80 dark:text-gray-400">
                      لا يوجد
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {oldData.perviousRequests.map((item) => (
                        <span
                          key={item?.ID}
                          className="rounded-md bg-gradient-to-r from-amber-100/80 to-amber-200/80 px-2 py-1 text-xs font-medium text-amber-700 shadow-sm dark:from-amber-900/30 dark:to-amber-800/30 dark:text-amber-300"
                        >
                          #{item?.ID}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-lg border border-amber-200/30 bg-white/80 shadow-sm dark:border-amber-700/30 dark:bg-gray-800/80">
                <div className="rounded-md p-2.5">
                  <h4 className="mb-2 flex items-center gap-2 text-sm font-medium text-amber-700 dark:text-amber-400">
                    <span className="inline-block h-1 w-4 rounded-full bg-amber-600 dark:bg-amber-400"></span>
                    التوريد
                  </h4>
                  {oldData?.perviousIncoming.length === 0 ? (
                    <div className="flex h-8 items-center justify-center rounded-md bg-gray-50/80 text-xs text-gray-500 dark:bg-gray-700/80 dark:text-gray-400">
                      لا يوجد
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {oldData.perviousIncoming.map((item) => (
                        <span
                          key={item?.ID}
                          className="rounded-md bg-gradient-to-r from-amber-100/80 to-amber-200/80 px-2 py-1 text-xs font-medium text-amber-700 shadow-sm dark:from-amber-900/30 dark:to-amber-800/30 dark:text-amber-300"
                        >
                          #{item?.ID}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default InvoiceHeader;
